import type { SnakeCasedPropertiesDeep } from 'type-fest';
import type { tellerscript as TellerScriptType } from '~/infra/api/rpc';
import { tellerscript as TellerScript } from '~/infra/api/rpc';
import { TextAlign, FontSize } from '~/models/tellerscript';
import { Color } from '~/utils/styles/enums';
import type {
  ChatScriptOperation,
  IScriptTextAlign,
  ChatScriptImage,
  PostImage,
  PostMessage,
  ShowImage,
  ShowNotification,
  ShowText,
  TypingNow,
  EndCall,
  MissCall,
  NoResponseCall,
  CharacterWithPosition,
  ShowTextFontSizeToken,
} from '@app/_types/chat-script';
import { convertRGBA } from './rgba-converter';
import type { IOp, PartialProtoImage } from './types';

const convertTextAlign = (textAlign: TextAlign): IScriptTextAlign => {
  switch (textAlign) {
    case TextAlign.TEXT_ALIGN_LEFT: {
      return 'left';
    }
    case TextAlign.TEXT_ALIGN_RIGHT: {
      return 'right';
    }
    default: {
      return 'center';
    }
  }
};

const convertFontSize = (fontSize: FontSize): ShowTextFontSizeToken => {
  switch (fontSize) {
    case FontSize.FONT_SIZE_X_SMALL: {
      return 'show_text_size_x_small';
    }
    case FontSize.FONT_SIZE_SMALL: {
      return 'show_text_size_small';
    }
    case FontSize.FONT_SIZE_MEDIUM: {
      return 'show_text_size_medium';
    }
    case FontSize.FONT_SIZE_LARGE: {
      return 'show_text_size_large';
    }
    case FontSize.FONT_SIZE_X_LARGE: {
      return 'show_text_size_x_large';
    }
  }
};

const convertToChatScriptImage = (
  image: PartialProtoImage
): ChatScriptImage => {
  return {
    imageUrl: image.serving_url ?? '',
    width: image.width ?? undefined,
    height: image.height ?? undefined,
  };
};

const convertToShowImage = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IShowImageOp>,
  imageResources: PartialProtoImage[]
): ShowImage | undefined => {
  const image = imageResources.find((image) => image.id === data.image_id);
  if (!image) return;

  return {
    operationType: 'show_image_op',
    image: convertToChatScriptImage(image),
    hasCharacter: false,
  };
};

const convertToShowText = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IShowTextOp>
): ShowText | undefined => {
  return {
    operationType: 'show_text_op',
    textAlign: convertTextAlign(data.text_align ?? TextAlign.TEXT_ALIGN_CENTER),
    text: data.text ?? '',
    fontColor: data.font_color
      ? convertRGBA(data.font_color)
      : Color.ACCENT_1000,
    backgroundColor: data.background_color
      ? convertRGBA(data.background_color)
      : 'rgba(0, 0, 0, 0)',
    fontSize: convertFontSize(data.font_size ?? FontSize.FONT_SIZE_MEDIUM),
    fontWeight:
      data.font_size === FontSize.FONT_SIZE_LARGE ||
      data.font_size === FontSize.FONT_SIZE_X_LARGE
        ? 'bold'
        : 'normal',
    hasCharacter: false,
  };
};

const convertToPostImage = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IPostImageOp>,
  imageResources: PartialProtoImage[],
  characterList: CharacterWithPosition[]
): PostImage | undefined => {
  const image = imageResources.find((image) => image.id === data.image_id);
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!image || !character) return;
  return {
    operationType: 'post_image_op',
    image: convertToChatScriptImage(image),
    character,
    hasCharacter: true,
  };
};

const convertToPostText = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IPostMessageOp>,
  characterList: CharacterWithPosition[],
  sceneType: TellerScriptType.Scene.Type | undefined
): PostMessage | undefined => {
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!character) return;
  const viewType =
    sceneType === TellerScript.Scene.Type.TYPE_TALK ? 'talk' : 'chat';
  return {
    operationType: 'post_message_op',
    text: data.text ?? '',
    viewType,
    character,
    hasCharacter: true,
  };
};

const convertToShowNotification = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IShowNotificationOp>,
  imageResources: PartialProtoImage[]
): ShowNotification | undefined => {
  const image = imageResources.find((image) => image.id === data.image_id);
  if (!image) return;

  return {
    operationType: 'show_notification_op',
    image: convertToChatScriptImage(image),
    title: data.title ?? '',
    text: data.text ?? '',
    hasCharacter: false,
  };
};

const TYPING_DEFAULT_DURATION = 0.3;
const convertToTypingNow = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.ITypingNowOp>,
  characterList: CharacterWithPosition[]
): TypingNow | undefined => {
  const durationString = data.duration?.replace('s', '');
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!character) return;
  return {
    operationType: 'typing_now_op',
    duration: Number.isNaN(Number(durationString))
      ? TYPING_DEFAULT_DURATION
      : Number(durationString),
    character,
    hasCharacter: true,
  };
};

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
  const formattedSeconds =
    remainingSeconds < 10
      ? `0${remainingSeconds}`
      : remainingSeconds.toString();

  if (hours > 0) {
    const formattedHours = hours < 10 ? `0${hours}` : hours.toString();
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  } else {
    return `${formattedMinutes}:${formattedSeconds}`;
  }
};

const convertToEndCall = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IEndCallOp>,
  characterList: CharacterWithPosition[]
): EndCall | undefined => {
  const durationString = data.duration?.replace('s', '');
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!character) return;
  return {
    operationType: 'end_call_op',
    durationText: formatTime(
      Number.isNaN(Number(durationString)) ? 0 : Number(durationString)
    ),
    character,
    hasCharacter: true,
  };
};

const convertToMissCall = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.IMissCallOp>,
  characterList: CharacterWithPosition[]
): MissCall | undefined => {
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!character) return;
  return {
    operationType: 'miss_call_op',
    character,
    hasCharacter: true,
  };
};

const convertToNoResponseCall = (
  data: SnakeCasedPropertiesDeep<TellerScriptType.INoResponseCallOp>,
  characterList: CharacterWithPosition[]
): NoResponseCall | undefined => {
  const character = characterList.find(
    (character) => character.id === data.character_id
  );
  if (!character) return;
  return {
    operationType: 'no_response_call_op',
    character,
    hasCharacter: true,
  };
};

export const convertToChatScriptOperation = (
  operation: IOp,
  imageResources: PartialProtoImage[],
  characterList: CharacterWithPosition[],
  sceneType: TellerScriptType.Scene.Type | undefined
): ChatScriptOperation | undefined => {
  if (operation.show_image_op) {
    return convertToShowImage(operation.show_image_op, imageResources);
  } else if (operation.show_text_op) {
    return convertToShowText(operation.show_text_op);
  } else if (operation.post_image_op) {
    return convertToPostImage(
      operation.post_image_op,
      imageResources,
      characterList
    );
  } else if (operation.post_message_op) {
    return convertToPostText(
      operation.post_message_op,
      characterList,
      sceneType
    );
  } else if (operation.show_notification_op) {
    return convertToShowNotification(
      operation.show_notification_op,
      imageResources
    );
  } else if (operation.typing_now_op) {
    return convertToTypingNow(operation.typing_now_op, characterList);
  } else if (operation.end_call_op) {
    return convertToEndCall(operation.end_call_op, characterList);
  } else if (operation.missed_call_op) {
    return convertToMissCall(operation.missed_call_op, characterList);
  } else if (operation.no_response_call_op) {
    return convertToNoResponseCall(
      operation.no_response_call_op,
      characterList
    );
  }
  return undefined;
};
